/** Lifecycle state of a background activity. */
export enum ActivityStatus {
    Pending = "pending",
    Running = "running",
    Completed = "completed",
    Failed = "failed"
}

/**
 * UI renderer key used by activityUIRegistry to pick the correct view component.
 *
 * Superset of ActivityEventKind: includes kinds that come from Firestore events
 * (AsyncTask, CIModelChange) plus kinds synthesized client-side (Invite, JoinRequest).
 */
export enum ActivityUIKind {
    AsyncTask = "async_task",
    CIModelChange = "continuous_improvement_model_change",
    Invite = "invite",
    JoinRequest = "join_request"
}

/**
 * UI-ready representation of a background activity.
 *
 * This is the normalized shape that every ActivitySource must produce,
 * decoupled from any backend-specific data model (Firestore, PubSub, etc).
 * Components like ActivityItem and ActivityTray render exclusively from this.
 */
export interface ActivityViewData {
    /** Unique identifier, used as React key. */
    id: string;
    /** Renderer key — determines which view component handles this item. */
    kind: ActivityUIKind;
    /** Primary display text for the activity item. */
    title: string;
    /** When the activity was created (used for sorting and timestamp display). */
    createdAt: Date;
    /** Context shown above the title (e.g. project or dataset name). */
    contextLabel?: string;
    /** Secondary text below the title (e.g. "3 renamed, 2 deleted"). */
    description?: string | null;
    /** Lifecycle state — only relevant for kinds with a status workflow. */
    status?: ActivityStatus;
    /** FontAwesome icon class (e.g. "far fa-cog"). */
    icon?: string;
    /** URL the user can navigate to when the activity completes (e.g. the new dataset page). */
    targetUrl?: string | null;
    /** Whether targetUrl should open in a new tab. */
    openInNewTab?: boolean;
    /** Progress indicator for running tasks (e.g. 237 of 500 images). */
    progress?: { current: number; total: number } | null;
    /** Human-readable error message, only present when status is "failed". */
    error?: string | null;
    /** When the activity finished (used for duration display). */
    completedAt?: Date | null;
    /**
     * Whether the activity has been read by the user (no unread dot when true).
     * Set by useWorkspaceActivities from Firestore ack state + optimistic local state.
     */
    read?: boolean;
    /** When true the item is pinned to the top of the list and shows a warning icon. */
    urgent?: boolean;
    /** Kind-specific data the registered view needs beyond the standard fields. */
    meta?: Record<string, unknown>;
}

/**
 * Contract for any backend that provides activities to the UI.
 *
 * Implementations must expose a reactive subscribe method so the hook can
 * merge multiple sources into a single sorted list. Each source is responsible
 * for converting its backend-specific data into ActivityViewData.
 *
 * @see AsyncTaskService for the Firestore-backed implementation.
 */
export interface ActivitySource {
    subscribe(callback: (activities: ActivityViewData[]) => void): () => void;
}
